(function () {
  const DESKTOP_QUERY = '(min-width: 768px)';
  const FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  if (customElements.get('gwt-announcement-bar')) {
    return;
  }

  class GwtAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = this.querySelector('[data-announcement-messages]');
      this.slides = this.messages ? Array.from(this.messages.querySelectorAll('[data-announcement-slide]')) : [];
      this.links = Array.from(this.querySelectorAll('[data-announcement-link]'));
      this.desktopLocalization = this.querySelector('[data-announcement-localization]');
      this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
      this.activeSlide = 0;
      this.isPaused = false;
      this.timer = null;
      this.scrollFrame = null;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onMediaChange = this.syncMobileUtilities.bind(this);
      this.onMouseEnter = () => this.pause();
      this.onMouseLeave = () => this.resume();
      this.onScroll = this.queueVisibleSlideUpdate.bind(this);

      this.bindEvents();
      this.initSlider();
      this.syncMobileUtilities();
    }

    disconnectedCallback() {
      this.clearTimer();

      if (this.messages) {
        this.messages.removeEventListener('mouseenter', this.onMouseEnter);
        this.messages.removeEventListener('mouseleave', this.onMouseLeave);
        this.messages.removeEventListener('focusin', this.onMouseEnter);
        this.messages.removeEventListener('focusout', this.onMouseLeave);
        this.messages.removeEventListener('scroll', this.onScroll);
      }

      if (this.desktopMedia.removeEventListener) {
        this.desktopMedia.removeEventListener('change', this.onMediaChange);
      } else if (this.desktopMedia.removeListener) {
        this.desktopMedia.removeListener(this.onMediaChange);
      }

      this.restoreLocalization();
      this.removeMobileUtilities();
    }

    bindEvents() {
      if (this.desktopMedia.addEventListener) {
        this.desktopMedia.addEventListener('change', this.onMediaChange);
      } else if (this.desktopMedia.addListener) {
        this.desktopMedia.addListener(this.onMediaChange);
      }

      if (!this.messages || this.slides.length <= 1) {
        return;
      }

      this.messages.addEventListener('mouseenter', this.onMouseEnter);
      this.messages.addEventListener('mouseleave', this.onMouseLeave);
      this.messages.addEventListener('focusin', this.onMouseEnter);
      this.messages.addEventListener('focusout', this.onMouseLeave);
      this.messages.addEventListener('scroll', this.onScroll, { passive: true });
    }

    initSlider() {
      if (!this.messages || this.slides.length === 0) {
        return;
      }

      this.updateVisibleSlide(0);

      if (this.slides.length <= 1 || this.prefersReducedMotion.matches) {
        return;
      }

      this.scheduleNextSlide();
    }

    scheduleNextSlide() {
      this.clearTimer();

      const delay = parseInt(this.dataset.slideDelay, 10);
      this.timer = window.setTimeout(() => {
        if (!this.isPaused && this.isInViewport()) {
          this.goToSlide(this.activeSlide + 1);
        }

        this.scheduleNextSlide();
      }, Number.isNaN(delay) ? 7000 : delay);
    }

    clearTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    goToSlide(index) {
      if (!this.messages || this.slides.length <= 1) {
        return;
      }

      const nextIndex = index >= this.slides.length ? 0 : index;
      const isWrappingToStart = nextIndex === 0 && this.activeSlide === this.slides.length - 1;

      if (isWrappingToStart) {
        const originalScrollBehavior = this.messages.style.scrollBehavior;
        this.messages.style.scrollBehavior = 'auto';
        this.messages.scrollLeft = this.slides[nextIndex].offsetLeft;
        this.messages.style.scrollBehavior = originalScrollBehavior;
      } else {
        this.messages.scrollTo({
          left: this.slides[nextIndex].offsetLeft,
          behavior: 'smooth'
        });
      }

      this.updateVisibleSlide(nextIndex);
    }

    queueVisibleSlideUpdate() {
      if (this.scrollFrame || this.slides.length <= 1) {
        return;
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const scrollLeft = this.messages.scrollLeft;
        let nearestIndex = 0;
        let nearestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - scrollLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        this.updateVisibleSlide(nearestIndex);
      });
    }

    updateVisibleSlide(index) {
      this.activeSlide = index;

      this.slides.forEach((slide, slideIndex) => {
        const isVisible = slideIndex === index;
        slide.classList.toggle('is-visible', isVisible);
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        slide.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
          if (isVisible) {
            if (element.dataset.announcementTabindex !== undefined) {
              const originalValue = element.dataset.announcementTabindex;
              if (originalValue === '') {
                element.removeAttribute('tabindex');
              } else {
                element.setAttribute('tabindex', originalValue);
              }
              delete element.dataset.announcementTabindex;
            }
          } else {
            if (element.dataset.announcementTabindex === undefined) {
              element.dataset.announcementTabindex = element.getAttribute('tabindex') || '';
            }
            element.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    pause() {
      this.isPaused = true;
    }

    resume() {
      this.isPaused = false;
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    syncMobileUtilities() {
      if (this.desktopMedia.matches) {
        this.restoreLocalization();
        this.removeMobileUtilities();
        return;
      }

      this.restoreLocalization();

      const hasLocalization = this.desktopLocalization && this.desktopLocalization.firstElementChild;
      if (this.links.length === 0 && !hasLocalization) {
        this.removeMobileUtilities();
        return;
      }

      const loginWrapper = document.querySelector('.side-menu .menu-login-btn');
      if (!loginWrapper || !loginWrapper.parentNode) {
        return;
      }

      const mobileWrapper = this.getMobileUtilities();
      mobileWrapper.innerHTML = '';

      if (this.links.length > 0) {
        const nav = document.createElement('nav');
        nav.setAttribute('aria-label', this.dataset.mobileLinksLabel || 'Announcement links');

        const list = document.createElement('ul');
        list.className = 'list-unstyled';

        this.links.forEach((link) => {
          const item = document.createElement('li');
          const mobileLink = link.cloneNode(true);
          mobileLink.className = 'gwt-announcement-bar__mobile-link';
          mobileLink.removeAttribute('data-announcement-link');
          item.appendChild(mobileLink);
          list.appendChild(item);
        });

        nav.appendChild(list);
        mobileWrapper.appendChild(nav);
      }

      if (hasLocalization) {
        const localizationWrapper = document.createElement('div');
        localizationWrapper.className = 'gwt-announcement-bar__mobile-localization';
        localizationWrapper.appendChild(this.desktopLocalization.firstElementChild);
        mobileWrapper.appendChild(localizationWrapper);
      }

      loginWrapper.parentNode.insertBefore(mobileWrapper, loginWrapper);
    }

    getMobileUtilities() {
      if (!this.mobileUtilities) {
        this.mobileUtilities = document.createElement('div');
        this.mobileUtilities.className = 'gwt-announcement-bar__mobile-utilities';
        this.mobileUtilities.dataset.gwtAnnouncementMobile = this.dataset.sectionId || '';
        this.mobileUtilities.style.setProperty(
          '--gwt-announcement-mobile-link-color',
          this.dataset.mobileLinkColor || 'inherit'
        );
      }

      return this.mobileUtilities;
    }

    restoreLocalization() {
      if (!this.desktopLocalization) {
        return;
      }

      const mobileLocalization = this.mobileUtilities
        ? this.mobileUtilities.querySelector('.gwt-announcement-bar__mobile-localization')
        : null;

      if (mobileLocalization && mobileLocalization.firstElementChild) {
        this.desktopLocalization.appendChild(mobileLocalization.firstElementChild);
      }
    }

    removeMobileUtilities() {
      if (this.mobileUtilities && this.mobileUtilities.parentNode) {
        this.mobileUtilities.parentNode.removeChild(this.mobileUtilities);
      }

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }
  }

  customElements.define('gwt-announcement-bar', GwtAnnouncementBar);
})();
